import { Record } from 'immutable';
import format from 'date-fns/format';

import Hydrator from 'utils/Hydrator';
import FundingDocument from './FundingDocumentModel';
import { ActivityStatus } from '../enums/activity-status';
import { ActivityType } from '../enums/activity-type';
import { ActivityRequiringAttentionType } from '../enums/activity-requiring-attention-type';

const ActivityRecord = Record({
  id: '',
  title: '',
  subtitle: '',
  type: 0,
  status: 0,
  fundingSearchId: 0,
  fundingMatchStatus: 0,
  documents: [],
  authorId: 0,
  authorName: '',
  notes: '',
  authorEntityId: 0,
  ownerEntityId: 0,
  documentTypeId: 0,
  requiringAttentionType: 0,
  selectedFeedbackAction: null,
  offerId: null,
  feedbackType: 0,
  lenderEntityId: 0,
  introducerEntityId: 0,
  fundingMatchId: 0,
  createdAt: new Date(),
  updatedAt: new Date(),
  snoozedUntil: null,
  caseId: null,
  caseType: null,
});

export default class Activity extends ActivityRecord {
  static deserialize(jsonObject = {}) {
    if (!jsonObject) {
      return undefined;
    }

    const mapProperties = {
      documents: Hydrator.fromArray(FundingDocument.deserialize),
      createdAt: Hydrator.timestamp2Date,
      updatedAt: Hydrator.timestamp2Date,
    };

    const hydrated = Object.keys(jsonObject).reduce(Hydrator.assign(jsonObject, mapProperties), {});

    return new Activity(hydrated);
  }

  get link() {
    if (!this.caseId || this.caseId === 'undefined') return undefined;

    if (this.caseType === 'FUNDING') return `/funding/search/${this.caseId}/profile`;
    if (this.caseType === 'NON-FUNDING') return `/cases/${this.caseId}`;

    return undefined;
  }

  getCreatedAtFormatted(formatStr = 'd MMMM yyyy') {
    return format(this.createdAt, formatStr);
  }

  getCreatedAtShort() {
    return this.getCreatedAtFormatted('d MMMM');
  }

  getUpdatedAtFormatted(formatStr = 'd MMMM yyyy') {
    return format(this.updatedAt, formatStr);
  }

  getUpdatedAtShort() {
    return this.getUpdatedAtFormatted('d MMMM');
  }

  isStatusDue() {
    return this.status === ActivityStatus.due;
  }

  isStatusCompleted() {
    return this.status === ActivityStatus.completed;
  }

  isStatusDismissed() {
    return this.status === ActivityStatus.dismissed;
  }

  isTypeDocuments() {
    return this.type === ActivityType.documents;
  }

  isTypeProfile() {
    return this.type === ActivityType.profile;
  }

  isTypeFundingMatch() {
    return this.type === ActivityType.fundingMatch;
  }

  isTypeFeedback() {
    return this.type === ActivityType.feedback;
  }

  isAction() {
    return this.isStatusDue();
  }

  isUpdate() {
    return !this.isStatusDue();
  }

  isLenderUpdate() {
    return this.requiringAttentionType === ActivityRequiringAttentionType.lenderUpdates;
  }

  isRequiringAttention() {
    return this.isStatusDue() || this.isLenderUpdate();
  }
}
